import React, { useMemo, useRef, useCallback } from 'react'
import { Tree, Empty } from 'antd'
import type { TreeDataNode } from 'antd'
import type { Key } from 'react'
import { useTranslation } from 'react-i18next'
import Highlighter from 'react-highlight-words'
import { flattenTree } from '../../utils/treeUtils'
import { useRemountOnVisible } from '../../hooks/useRemountOnVisible'

export type ShiftClickMode = 'all' | 'sameLevel' | 'leafOnly'

export interface StringTitleTreeNode extends Omit<TreeDataNode, 'title' | 'children'> {
  title: string
  children?: StringTitleTreeNode[]
}

export interface CheckboxTreeProps {
  treeData: StringTitleTreeNode[]
  value?: string[]
  onChange?: (values: string[]) => void
  /** External search text for filtering and highlighting */
  searchText?: string
  height?: number
  /** How shift+click range selection works: 'all' selects everything between (default), 'sameLevel' selects only siblings, 'leafOnly' selects only leaf nodes between */
  shiftClickMode?: ShiftClickMode
}

/** Compute half-checked keys: parent is half-checked when some (not all) descendants are checked */
const getHalfCheckedKeys = (nodes: StringTitleTreeNode[], checkedSet: Set<string>): string[] => {
  if (checkedSet.size === 0) return []

  const result: string[] = []

  const traverse = (node: StringTitleTreeNode): { total: number; checked: number } => {
    if (!node.children || node.children.length === 0) {
      return { total: 1, checked: checkedSet.has(String(node.key)) ? 1 : 0 }
    }

    let total = 1
    let checked = checkedSet.has(String(node.key)) ? 1 : 0

    for (const child of node.children) {
      const counts = traverse(child)
      total += counts.total
      checked += counts.checked
    }

    if (checked > 0 && checked < total && !checkedSet.has(String(node.key))) {
      result.push(String(node.key))
    }

    return { total, checked }
  }

  for (const node of nodes) {
    traverse(node)
  }

  return result
}

/** Filter tree nodes, keeping parents whose children match */
const filterTree = (nodes: StringTitleTreeNode[], search: string): StringTitleTreeNode[] => {
  const lower = search.toLowerCase()
  return nodes.reduce<StringTitleTreeNode[]>((acc, node) => {
    const titleMatch = node.title.toLowerCase().includes(lower)
    const filteredChildren = node.children ? filterTree(node.children, search) : []

    if (titleMatch || filteredChildren.length > 0) {
      acc.push({
        ...node,
        children: filteredChildren.length > 0 ? filteredChildren : node.children
      })
    }
    return acc
  }, [])
}

/** Collect all parent keys (nodes that have children) from tree */
const getParentKeys = (nodes: StringTitleTreeNode[]): string[] => {
  const keys: string[] = []
  const traverse = (items: StringTitleTreeNode[]) => {
    for (const node of items) {
      if (node.children && node.children.length > 0) {
        keys.push(String(node.key))
        traverse(node.children)
      }
    }
  }
  traverse(nodes)
  return keys
}

/** Find the sibling keys list that contains the given key */
const getSiblingKeys = (nodes: StringTitleTreeNode[], targetKey: string): string[] | null => {
  const keys = nodes.map(n => String(n.key))
  if (keys.includes(targetKey)) return keys

  for (const node of nodes) {
    if (node.children) {
      const found = getSiblingKeys(node.children, targetKey)
      if (found) return found
    }
  }
  return null
}

/** Collect leaf keys (nodes without children) in depth-first order */
const getLeafKeys = (nodes: StringTitleTreeNode[]): string[] => {
  const keys: string[] = []
  const traverse = (items: StringTitleTreeNode[]) => {
    for (const node of items) {
      if (node.children && node.children.length > 0) {
        traverse(node.children)
      } else {
        keys.push(String(node.key))
      }
    }
  }
  traverse(nodes)
  return keys
}

/** Checkbox tree with shift+click range selection and half-checked state */
const CheckboxTree: React.FC<CheckboxTreeProps> = ({
  treeData,
  value = [],
  onChange,
  searchText = '',
  height = 250,
  shiftClickMode = 'all'
}) => {
  const { t } = useTranslation()
  const [expandedKeys, setExpandedKeys] = React.useState<Key[]>([])
  const shiftAnchorRef = useRef<string | null>(null)
  const { ref: containerRef, key: treeKey } = useRemountOnVisible<HTMLDivElement>()

  // Auto-expand root node on first render
  React.useEffect(() => {
    if (expandedKeys.length === 0 && treeData.length > 0) {
      setExpandedKeys([treeData[0].key])
    }
  }, [treeData])

  const filteredTreeData = useMemo(() => {
    if (!searchText) return treeData
    return filterTree(treeData, searchText)
  }, [treeData, searchText])

  React.useEffect(() => {
    if (searchText) {
      setExpandedKeys(getParentKeys(filteredTreeData))
    }
  }, [filteredTreeData])

  const flatKeys = useMemo(
    () => flattenTree(filteredTreeData, { valueField: 'key', childrenField: 'children' }),
    [filteredTreeData]
  )

  const halfCheckedKeys = useMemo(() => {
    return getHalfCheckedKeys(treeData, new Set(value))
  }, [treeData, value])

  const handleCheck = useCallback(
    (
      checkedKeysValue: Key[] | { checked: Key[]; halfChecked: Key[] },
      info: { node: { key: Key }; checked: boolean; nativeEvent?: MouseEvent }
    ) => {
      const keys = Array.isArray(checkedKeysValue) ? checkedKeysValue : checkedKeysValue.checked
      const checkedKey = String(info.node.key)

      if (info.nativeEvent?.shiftKey && shiftAnchorRef.current) {
        let rangeSource: string[]
        switch (shiftClickMode) {
          case 'sameLevel':
            rangeSource = getSiblingKeys(filteredTreeData, checkedKey) ?? flatKeys
            break
          case 'leafOnly':
            rangeSource = getLeafKeys(filteredTreeData)
            break
          default:
            rangeSource = flatKeys
        }

        const anchorIdx = rangeSource.indexOf(shiftAnchorRef.current)
        const currentIdx = rangeSource.indexOf(checkedKey)

        if (anchorIdx !== -1 && currentIdx !== -1) {
          const start = Math.min(anchorIdx, currentIdx)
          const end = Math.max(anchorIdx, currentIdx)
          const rangeKeys = rangeSource.slice(start, end + 1)
          const isChecking = info.checked

          const newKeys = isChecking
            ? Array.from(new Set([...value, ...rangeKeys]))
            : value.filter(k => !rangeKeys.includes(k))

          onChange?.(newKeys)
          shiftAnchorRef.current = checkedKey
          return
        }
      }

      shiftAnchorRef.current = checkedKey
      onChange?.(keys.map(String))
    },
    [flatKeys, filteredTreeData, shiftClickMode, value, onChange]
  )

  if (filteredTreeData.length === 0) {
    return <Empty description={t('global:no-results')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
  }

  return (
    <div ref={containerRef} style={{ userSelect: 'none' }}>
      <Tree
        key={treeKey}
        checkable
        checkStrictly
        height={height}
        selectable={false}
        expandedKeys={expandedKeys}
        onExpand={keys => setExpandedKeys(keys)}
        checkedKeys={{ checked: value, halfChecked: halfCheckedKeys }}
        onCheck={handleCheck}
        treeData={filteredTreeData}
        titleRender={node => {
          const { title } = node as StringTitleTreeNode
          return (
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
              {searchText ? <Highlighter searchWords={[searchText]} autoEscape textToHighlight={title} /> : title}
            </div>
          )
        }}
      />
    </div>
  )
}

export default CheckboxTree
